import { Avatar, Typography } from '@mui/material'
import ExpandMore from '@mui/icons-material/ExpandMoreRounded'
import { memo } from 'react'
import styled from 'styled-components'
import { colorPalette } from '../../../../style/colorPalette'
import { Swimlane, SwimlaneCumulationData } from '../../Kanban'
import { roundNumber } from '../../../../../utils/number'
import { intl } from '../../../../../i18n'

type Props = {
  swimlane: Swimlane
  cumulation: SwimlaneCumulationData
  isCollapsed: boolean
  onToggleCollapse: () => void
}

export const SwimlaneSectionHeader = memo(
  ({ swimlane, cumulation, isCollapsed, onToggleCollapse }: Props) => {
    const formattedEstimatedHour = roundNumber(cumulation.estimatedHour, 1)
    const formattedActualHour = roundNumber(cumulation.actualHour, 1)

    return (
      <HeaderRow onClick={onToggleCollapse}>
        <CollapseIcon isCollapsed={isCollapsed}>
          <ExpandMore />
        </CollapseIcon>
        {swimlane.responsibleUuid && (
          <StyledAvatar src={swimlane.responsibleIconUrl}>
            {swimlane.responsibleName?.charAt(0) ?? '?'}
          </StyledAvatar>
        )}
        <ResponsibleName>{swimlane.responsibleName}</ResponsibleName>
        <CumulationText>
          {intl.formatMessage(
            { id: 'kanban.column.header.hitShort' },
            { total: cumulation.count }
          )}{' '}
          |{' '}
          {intl.formatMessage(
            { id: 'kanban.column.header.estimatedHour' },
            { estimatedHour: formattedEstimatedHour }
          )}{' '}
          /{' '}
          {intl.formatMessage(
            { id: 'kanban.column.header.actualHour' },
            { actualHour: formattedActualHour }
          )}
        </CumulationText>
      </HeaderRow>
    )
  }
)

const HeaderRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '4px 10px',
  backgroundColor: colorPalette.monotone[1],
  borderRadius: '4px',
  height: '28px',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: colorPalette.monotone[2],
  },
})

const CollapseIcon = styled('div')<{ isCollapsed: boolean }>(props => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '20px',
  height: '20px',
  color: colorPalette.monotone[5],
  '& .MuiSvgIcon-root': {
    fontSize: '18px',
    transform: props.isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
    transition: 'transform 0.2s ease',
  },
}))

const StyledAvatar = styled(Avatar)({
  width: 20,
  height: 20,
  fontSize: 10,
  color: colorPalette.monotone[10],
})

const ResponsibleName = styled(Typography)({
  fontWeight: 600,
  fontSize: '13px',
  lineHeight: '20px',
  color: colorPalette.monotone[10],
})

const CumulationText = styled(Typography)({
  fontSize: '12px',
  lineHeight: '20px',
  color: colorPalette.monotone[5],
})
